import { getExtractionFieldsToSend } from 'app/shared/components/document/ExtractionField/EditExtractionFields/EditExtractionFields.utils';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { GROUP_DIVIDER, RESULT_FIELD_ID_PARTS_DIVIDER } from 'constants/documentExtractionFields';
import { DOC_FIELD_TYPES } from 'constants/documentFieldTypes';
import { formatCalendarDate } from 'utils/date';
import { checkIsContainerType } from 'utils/documentFieldTypes';
import { formatNumberToCurrencyString, formatNumberToFixed2DecimalsExceptNull } from 'utils/number';

import { WORKSHEET_FIELD_FORMAT } from './components/WorksheetFieldsLayout/WorksheetFieldsLayout.constants';
import {
  buildWorksheetSections,
  isWorksheetFieldVisible,
  parseWorksheetFieldSettings,
} from './components/WorksheetFieldsLayout/WorksheetFieldsLayout.utils';
import {
  WORKSHEET_PRINT_ANSWERS_TITLE,
  WORKSHEET_EMPTY_VALUE,
  WORKSHEET_CALCULATED_VALUES_TITLE,
  WORKSHEET_PRINT_LOAN_FACTS_TITLE,
  WORKSHEET_PRINT_NO,
  WORKSHEET_PRINT_YES,
  WORKSHEET_TABLE_ROW_GROUP_PREFIX,
} from './WorksheetDrawer.constants';

// Calculated values arrive as invariant-culture text; only plain decimals are numbers.
const DECIMAL_TEXT_REGEX = /^-?\d+(\.\d+)?$/;

/**
 * A calculated value as the worksheet shows it: plain decimals to two places, other text as-is,
 * nothing as a dash. The saved PDF formats them the same way.
 *
 * @param {string|null} value invariant-culture text
 * @returns {string}
 */
export const formatWorksheetCalculatedValue = value => {
  if (value === null || value === undefined || value === '') {
    return WORKSHEET_EMPTY_VALUE;
  }

  return DECIMAL_TEXT_REGEX.test(value)
    ? formatNumberToFixed2DecimalsExceptNull(Number(value))
    : value;
};

// A table row added on the form clones the table's columns under ids suffixed with the row's group.
const getBaseFieldId = id => `${id}`.split(RESULT_FIELD_ID_PARTS_DIVIDER)[0];

// The form key of a table cell: row 1 is the table's own columns, every later row a cloned group of
// them — the key shape the extraction form rebuilds its added rows from on reopen.
const getWorksheetTableCellKey = ({ columnId, tableId, rowNumber }) =>
  rowNumber > 1
    ? `${columnId}${RESULT_FIELD_ID_PARTS_DIVIDER}${tableId}${GROUP_DIVIDER}${WORKSHEET_TABLE_ROW_GROUP_PREFIX}${rowNumber}`
    : columnId;

/**
 * Flattens a document's filled extraction results into a form map keyed by the
 * extraction-field id, so an existing worksheet's values pre-populate the reused
 * extraction form on reopen. The first result per field id wins.
 *
 * A table's cells (LEN-194: repeating rows) are keyed per row (see getWorksheetTableCellKey), so the
 * form rebuilds each saved row; the table's own empty row marker is skipped. Other Container
 * results are skipped, as buildWorksheetSavePayload sends none. Dropdown answers are saved as
 * their option name(s) — text — so they pass through here and normalizeWorksheetInitialForm turns
 * them back into options.
 *
 * @param {Array<{documentExtractionFieldId: string, value: *, rowNumber?: number}>} filledExtractionFields
 * @param {Array<{id: string, type: string, parentId?: string}>} extractionFields the type's fields
 * @returns {Object} form map { [fieldId]: value }
 */
export const buildWorksheetInitialForm = (filledExtractionFields = [], extractionFields = []) => {
  const tableIds = new Set(
    extractionFields.filter(({ type }) => type === DOC_FIELD_TYPES.TABLE).map(({ id }) => id),
  );
  const tableIdByColumnId = _.fromPairs(
    extractionFields
      .filter(({ parentId }) => tableIds.has(parentId))
      .map(({ id, parentId }) => [id, parentId]),
  );

  return (filledExtractionFields ?? []).reduce((acc, result) => {
    const { documentExtractionFieldId, value, rowNumber } = result ?? {};
    const isScalar = _.isString(value) || _.isNumber(value) || _.isBoolean(value);

    if (!documentExtractionFieldId || tableIds.has(documentExtractionFieldId) || !isScalar) {
      return acc;
    }

    const tableId = tableIdByColumnId[documentExtractionFieldId];
    const key = tableId
      ? getWorksheetTableCellKey({
          columnId: documentExtractionFieldId,
          tableId,
          rowNumber: rowNumber ?? 1,
        })
      : documentExtractionFieldId;

    if (!_.has(acc, key)) {
      acc[key] = value;
    }

    return acc;
  }, {});
};

/**
 * Filled results carry every value as text, so a Bool field comes back as 'true' / 'false' — and the
 * checkbox renders any non-empty string as checked — and a dropdown comes back as its option name
 * ('a;b' for several), while the dropdown reads the option field itself. Converts those fields of
 * the form using the document type's extraction-field definitions (dropdown options are listed as
 * fields whose parentId is the dropdown); other fields are left untouched.
 *
 * @param {Object|null} initialForm form map { [fieldId]: value }
 * @param {Array<{id: string, type: string, name?: string, parentId?: string}>} extractionFields
 * @returns {Object|null}
 */
export const normalizeWorksheetInitialForm = (initialForm, extractionFields = []) => {
  if (!initialForm) {
    return initialForm;
  }

  const typeByFieldId = _.mapValues(_.keyBy(extractionFields, 'id'), 'type');
  const optionsByFieldId = _.groupBy(
    extractionFields.filter(({ type }) => type === DOC_FIELD_TYPES.DROP_DOWN_FIELD),
    'parentId',
  );

  // A table row added on the form clones its dropdown's options under the row's suffix too, so the
  // option comes back under that suffix for the row's dropdown to show it (LEN-194).
  const findOption = (fieldId, name) => {
    const option = _.find(
      optionsByFieldId[getBaseFieldId(fieldId)],
      ({ name: optionName }) => optionName === name,
    );
    const rowSuffix = `${fieldId}`.slice(getBaseFieldId(fieldId).length);

    return option ? { ...option, id: `${option.id}${rowSuffix}` } : null;
  };

  return _.mapValues(initialForm, (value, fieldId) => {
    if (!_.isString(value)) {
      return value;
    }

    switch (typeByFieldId[getBaseFieldId(fieldId)]) {
      case DOC_FIELD_TYPES.BOOL:
        return value.toLowerCase() === 'true';
      case DOC_FIELD_TYPES.SINGLE_SELECTION_DROP_DOWN:
        return findOption(fieldId, value);
      case DOC_FIELD_TYPES.MULTIPLE_SELECTION_DROP_DOWN:
        return _.compact(value.split(';').map(name => findOption(fieldId, name)));
      default:
        return value;
    }
  });
};

/**
 * Builds the worksheet save payload for the Documents worksheet endpoint from the
 * reused extraction form state. The endpoint's WorksheetFieldValueDto contract is
 * `{ value, fieldId }` — scalar values keyed by extraction-field id — so the
 * extraction-results entries produced by getExtractionFieldsToSend are remapped.
 * Only scalar-valued entries are sent: table containers/rows (entries carrying
 * `children`) have no worksheet DTO representation and are skipped.
 */
const buildWorksheetValues = ({ form, documentExtractionFieldsTree }) => {
  const { extractionFields } = getExtractionFieldsToSend({
    form,
    documentExtractionFieldsTree,
  });

  return extractionFields.reduce((acc, { extractionFieldId, value, children }) => {
    const isScalar = _.isString(value) || _.isNumber(value) || _.isBoolean(value);

    if (!extractionFieldId || children?.length || !isScalar || value === '') {
      return acc;
    }

    acc.push({ fieldId: extractionFieldId, value: String(value) });

    return acc;
  }, []);
};

const toWorksheetCellValue = value => {
  if (_.isArray(value)) {
    return value.map(({ name }) => name).join(';') || null;
  }

  if (_.isObject(value)) {
    return value.name ?? null;
  }

  return _.isNil(value) || value === '' ? null : String(value);
};

const findWorksheetTables = fields =>
  fields.flatMap(field => {
    if (field.type === DOC_FIELD_TYPES.TABLE) {
      return [field];
    }

    return field.type === DOC_FIELD_TYPES.CONTAINER
      ? findWorksheetTables(field.nestedFields ?? [])
      : [];
  });

// Row 1 is the table's own columns; every row added on the form is a group of cloned columns.
const getWorksheetTableRows = ({ nestedFields = [], nestedGroups = {} }) => [
  nestedFields,
  ...Object.values(nestedGroups ?? {}),
];

const buildWorksheetTableRowValues = ({ rowFields, form }) =>
  rowFields
    .filter(({ type }) => !checkIsContainerType(type) && type !== DOC_FIELD_TYPES.DROP_DOWN_FIELD)
    .flatMap(({ id }) => {
      const value = toWorksheetCellValue(form?.[id]);

      return _.isNil(value) ? [] : [{ fieldId: getBaseFieldId(id), value }];
    });

/**
 * The worksheet's tables (LEN-194: repeating rows, e.g. the Chattel Funding Worksheet's
 * disbursements) for the Documents worksheet endpoint: `{ fieldId, rows: [{ values: [{ fieldId,
 * value }] }] }`, rows in form order. Rows the user left blank are dropped, as the endpoint does, so
 * the remaining rows are numbered 1..n the same on both sides.
 */
export const buildWorksheetTables = ({ form, documentExtractionFieldsTree = [] }) =>
  findWorksheetTables(documentExtractionFieldsTree)
    .map(table => ({
      fieldId: table.id,
      rows: getWorksheetTableRows(table)
        .map(rowFields => ({ values: buildWorksheetTableRowValues({ rowFields, form }) }))
        .filter(({ values }) => values.length),
    }))
    .filter(({ rows }) => rows.length);

/**
 * The values a worksheet's calculation fields are evaluated on: the scalar answers plus every table
 * cell with its 1-based row number, so a calculation field can total a table's rows.
 */
export const buildWorksheetCalculationValues = ({ form, documentExtractionFieldsTree }) => [
  ...buildWorksheetValues({ form, documentExtractionFieldsTree }),
  ...buildWorksheetTables({ form, documentExtractionFieldsTree }).flatMap(({ rows }) =>
    rows.flatMap(({ values }, index) => values.map(value => ({ ...value, rowNumber: index + 1 }))),
  ),
];

export const buildWorksheetSavePayload = ({
  documentTypeId,
  applicationId,
  loanId,
  completed,
  form,
  documentExtractionFieldsTree,
}) => ({
  documentTypeId,
  applicationId,
  loanId,
  completed,
  values: buildWorksheetValues({ form, documentExtractionFieldsTree }),
  tables: buildWorksheetTables({ form, documentExtractionFieldsTree }),
});

const formatPrintedAnswer = ({ type, format, value }) => {
  if (type === DOC_FIELD_TYPES.BOOL) {
    if (!_.isBoolean(value)) {
      return null;
    }

    return value ? WORKSHEET_PRINT_YES : WORKSHEET_PRINT_NO;
  }

  if (_.isArray(value)) {
    return value.map(({ name }) => name).join(', ') || null;
  }

  if (_.isObject(value)) {
    return value.name ?? null;
  }

  if (type === DOC_FIELD_TYPES.DATE && value) {
    return formatCalendarDate(value, COMMON_DATE_FORMAT);
  }

  // Same rule as the saved PDF: a `format: currency` amount prints as dollars.
  if (format === WORKSHEET_FIELD_FORMAT.CURRENCY && DECIMAL_TEXT_REGEX.test(String(value ?? ''))) {
    return formatNumberToCurrencyString(Number(value));
  }

  return _.isNil(value) || value === '' ? null : String(value);
};

const isPrintedQuestion = ({ type }) => type !== DOC_FIELD_TYPES.DROP_DOWN_FIELD;

// One printed line per filled row — "Disbursements 2: Payee Type: Title; Amount: 25.00" — as the
// saved PDF prints it.
const buildTablePrintRows = ({ field, form }) =>
  getWorksheetTableRows(field)
    .map(rowFields =>
      rowFields
        .filter(({ type }) => !checkIsContainerType(type) && isPrintedQuestion({ type }))
        .flatMap(cell => {
          const value = formatPrintedAnswer({
            type: cell.type,
            format: parseWorksheetFieldSettings(cell.description).format,
            value: form?.[cell.id],
          });

          return _.isNil(value) ? [] : [`${cell.name}: ${value}`];
        })
        .join('; '),
    )
    .filter(Boolean)
    .map((value, index) => ({ label: `${field.name} ${index + 1}`, value }));

const buildPrintRows = ({ items, fieldsByName, form }) =>
  items.flatMap(({ field, settings }) => {
    if (!isPrintedQuestion(field)) {
      return [];
    }

    if (field.type === DOC_FIELD_TYPES.TABLE) {
      return isWorksheetFieldVisible({ showIf: settings.showIf, fieldsByName, form })
        ? buildTablePrintRows({ field, form })
        : [];
    }

    if (field.type === DOC_FIELD_TYPES.CONTAINER) {
      const nestedFields = field.nestedFields ?? [];

      return buildPrintRows({
        items: nestedFields.map(nestedField => ({
          field: nestedField,
          settings: parseWorksheetFieldSettings(nestedField.description),
        })),
        fieldsByName: _.keyBy(nestedFields, 'name'),
        form,
      });
    }

    if (!isWorksheetFieldVisible({ showIf: settings.showIf, fieldsByName, form })) {
      return [];
    }

    return [
      {
        label: field.name,
        value: formatPrintedAnswer({
          type: field.type,
          format: settings.format,
          value: form?.[field.id],
        }),
      },
    ];
  });

/**
 * The worksheet's questions and current answers in form order, for the printed copy, grouped into
 * the same sections as on screen (see buildWorksheetSections). Containers contribute their own
 * fields to their section and tables one line per filled row; dropdown options are not questions; follow-up questions
 * hidden by their showIf setting are left out, as on screen. Sections left with no rows are dropped.
 *
 * @param {{ form: Object, documentExtractionFieldsTree: Array }} params
 * @returns {Array<{ title: string|null, rows: Array<{ label: string, value: string|null }> }>}
 */
export const buildWorksheetPrintAnswers = ({ form, documentExtractionFieldsTree = [] }) => {
  const fieldsByName = _.keyBy(documentExtractionFieldsTree, 'name');

  return buildWorksheetSections(documentExtractionFieldsTree)
    .map(({ title, items }) => ({
      title,
      rows: buildPrintRows({ items, fieldsByName, form }),
    }))
    .filter(({ rows }) => rows.length);
};

const buildPrintRowsHtml = rows =>
  rows
    .map(
      ({ label, value }) =>
        `<tr><td style="width:40%;padding:4px 12px 4px 0;color:#4b5563;vertical-align:top;">${_.escape(
          label,
        )}</td><td style="padding:4px 0;font-weight:500;">${_.escape(
          value ?? WORKSHEET_EMPTY_VALUE,
        )}</td></tr>`,
    )
    .join('');

const buildPrintSectionHtml = ({ title, rows }) =>
  `${
    title ? `<h3 style="margin:16px 0 4px;font-size:14px;">${_.escape(title)}</h3>` : ''
  }<table style="border-collapse:collapse;table-layout:fixed;width:100%;font-size:12px;">${buildPrintRowsHtml(
    rows,
  )}</table>`;

const buildPrintGroupHtml = (title, sectionsHtml) =>
  `<h2 style="margin:24px 0 0;font-size:16px;">${_.escape(title)}</h2>${sectionsHtml}`;

/**
 * A standalone printable document of the worksheet — the loan facts it was taken against, every
 * question with its current answer by section, and the calculated values — that opens the
 * browser's print dialog on load (Save as PDF).
 *
 * @param {{
 *   title: string,
 *   printedAt: string,
 *   loanFactsSections: Array,
 *   answerSections: Array<{ title: string|null, rows: Array }>,
 *   calculatedValues: Array<{ name: string, value: string|null }>,
 * }} params
 * @returns {string}
 */
export const buildWorksheetPrintDocument = ({
  title,
  printedAt,
  loanFactsSections = [],
  answerSections = [],
  calculatedValues = [],
}) => {
  const loanFactsHtml = loanFactsSections.length
    ? buildPrintGroupHtml(
        WORKSHEET_PRINT_LOAN_FACTS_TITLE,
        loanFactsSections.map(buildPrintSectionHtml).join(''),
      )
    : '';
  const answersHtml = buildPrintGroupHtml(
    WORKSHEET_PRINT_ANSWERS_TITLE,
    answerSections.map(buildPrintSectionHtml).join(''),
  );
  const calculatedValuesHtml = calculatedValues.length
    ? buildPrintGroupHtml(
        WORKSHEET_CALCULATED_VALUES_TITLE,
        buildPrintSectionHtml({
          title: '',
          rows: calculatedValues.map(({ name, value }) => ({
            label: name,
            value: formatWorksheetCalculatedValue(value),
          })),
        }),
      )
    : '';

  return `<!doctype html><html><head><meta charset="utf-8"><title>${_.escape(
    title,
  )}</title></head><body onload="window.focus();window.print();" style="margin:32px;font-family:Arial,sans-serif;color:#111827;"><h1 style="margin:0 0 4px;font-size:20px;">${_.escape(
    title,
  )}</h1><p style="margin:0;color:#6b7280;font-size:12px;">${_.escape(
    printedAt,
  )}</p>${loanFactsHtml}${answersHtml}${calculatedValuesHtml}</body></html>`;
};
